<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { api } from '../api'
import { useI18n } from '../i18n'
import NaftaPosInstanceSelector from '../components/NaftaPosInstanceSelector.vue'
import { chooseNaftaPosSource, session } from '../session'
import { activeNaftaPosInstance } from '../platform/context'
import IndicatorSummaryCards from '../components/indicators/IndicatorSummaryCards.vue'
import CashFlowPanel from '../components/indicators/CashFlowPanel.vue'
import TankLevelsPanel from '../components/indicators/TankLevelsPanel.vue'
import CurrentCountersTable from '../components/indicators/CurrentCountersTable.vue'

const activeStation = computed(() => activeNaftaPosInstance(session.platformContext))
const snapshot = ref(null)
const loading = ref(true)
const switching = ref(false)
const error = ref('')
const { t } = useI18n()
const hasOpenShift = computed(() => snapshot.value?.shift?.state === 'open')
let requestNumber = 0
let controller = null

const snapshotScope = () => JSON.stringify([session.user?.id ?? session.user?.username,
  session.user?.accessVersion, session.platformContext?.activeStationId,
  activeStation.value?.id, activeStation.value?.enabled])

function sourceTime(value) {
  return value ? new Intl.DateTimeFormat('uk-UA', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '—'
}

function warningMessage(warning) {
  return warning.code === 'active_shift_missing' ? t('activeShiftMissing') : warning.message
}

async function loadSnapshot() {
  const currentRequest = ++requestNumber
  controller?.abort()
  error.value = ''
  snapshot.value = null
  if (!activeStation.value?.enabled) {
    loading.value = false
    return
  }
  controller = new AbortController()
  loading.value = true
  const scope = snapshotScope()
  const isCurrent = () => currentRequest === requestNumber && scope === snapshotScope()
  try {
    const result = await api('/online-indicators/current', { signal: controller.signal })
    if (isCurrent()) snapshot.value = result
  } catch (cause) {
    if (cause.name !== 'AbortError' && isCurrent()) error.value = cause.message
  } finally {
    if (isCurrent()) loading.value = false
  }
}

async function selectStation(profileId) {
  if (!profileId || activeStation.value?.id === profileId || switching.value) return
  switching.value = true
  requestNumber++
  controller?.abort()
  loading.value = false
  error.value = ''
  snapshot.value = null
  try {
    await chooseNaftaPosSource(profileId)
  } catch (cause) {
    error.value = cause.message
  } finally {
    switching.value = false
  }
}

onMounted(loadSnapshot)
watch(snapshotScope, loadSnapshot, { flush: 'sync' })
onBeforeUnmount(() => { requestNumber++; controller?.abort() })
</script>

<template>
  <section class="current-indicators-page">
    <div class="indicators-toolbar">
      <div><p class="eyebrow">Онлайн показники</p><h1>Поточні показники</h1><p v-if="snapshot" class="source-status" :class="`freshness-${snapshot.freshness}`">Оновлено: {{ sourceTime(snapshot.sampledAt) }}</p></div>
      <div class="indicator-actions"><NaftaPosInstanceSelector :context="session.platformContext" :disabled="switching || loading" @select="selectStation" /><button class="button refresh-button" type="button" :disabled="switching || loading || !activeStation?.enabled" @click="loadSnapshot">{{ loading ? 'Оновлюємо…' : 'Оновити' }}</button></div>
    </div>

    <p v-if="!activeStation && !loading" class="notice info">Оберіть робоче місце NaftaPOS цієї АЗС, щоб переглянути поточні показники.</p>
    <p v-if="error" class="notice error" role="alert">{{ error }} <button class="inline-retry" type="button" @click="loadSnapshot">Спробувати ще раз</button></p>
    <div v-if="loading" class="dashboard-loading" role="status"><p>Отримуємо поточні дані з АЗС…</p></div>
    <template v-else-if="snapshot">
      <div v-if="snapshot.warnings?.length" class="snapshot-warnings" role="status"><p v-for="warning in snapshot.warnings" :key="`${warning.section}-${warning.code}`">{{ warningMessage(warning) }}</p></div>
      <IndicatorSummaryCards :shift="hasOpenShift ? snapshot.shift : null" :empty-message="t('indicatorDataAbsent')" />
      <CashFlowPanel :cash-flow="hasOpenShift ? snapshot.cashFlow : null" :empty-message="t('indicatorDataAbsent')" />
      <TankLevelsPanel :tanks="hasOpenShift ? snapshot.tanks : []" :empty-message="t('indicatorDataAbsent')" />
      <CurrentCountersTable :counters="hasOpenShift ? snapshot.counters : []" :empty-message="t('indicatorDataAbsent')" />
    </template>
  </section>
</template>
